import { Node, Edge, MarkerType } from 'reactflow';

// BPMN Element type mappings
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const BPMN_TO_INTERNAL_TYPE: Record<string, string> = {
  'startEvent': 'event',
  'endEvent': 'event',
  'intermediateThrowEvent': 'event',
  'intermediateCatchEvent': 'event',
  'userTask': 'process',
  'serviceTask': 'process',
  'scriptTask': 'process',
  'sendTask': 'process',
  'receiveTask': 'process',
  'manualTask': 'process',
  'businessRuleTask': 'process',
  'exclusiveGateway': 'gateway',
  'parallelGateway': 'gateway',
  'inclusiveGateway': 'gateway',
  'eventBasedGateway': 'gateway',
  'complexGateway': 'gateway',
  'lane': 'lane',
  'participant': 'pool',
  'dataObjectReference': 'data-object',
  'dataStoreReference': 'data-object',
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const INTERNAL_TO_BPMN_TYPE: Record<string, string> = {
  'event-start': 'startEvent',
  'event-end': 'endEvent',
  'event-intermediate': 'intermediateThrowEvent',
  'process-user': 'userTask',
  'process-service': 'serviceTask',
  'process-script': 'scriptTask',
  'process-send': 'sendTask',
  'process-receive': 'receiveTask',
  'process-manual': 'manualTask',
  'process-business-rule': 'businessRuleTask',
  'gateway-exclusive': 'exclusiveGateway',
  'gateway-parallel': 'parallelGateway',
  'gateway-inclusive': 'inclusiveGateway',
  'gateway-event-based': 'eventBasedGateway',
  'gateway-complex': 'complexGateway',
  'lane': 'lane',
  'pool': 'participant',
  'data-object': 'dataObjectReference',
};

// Helper function to determine event type
function getEventType(element: Element): 'start' | 'intermediate' | 'end' | 'boundary' {
  const tagName = element.tagName.toLowerCase();
  if (tagName.includes('startevent')) return 'start';
  if (tagName.includes('endevent')) return 'end';
  if (tagName.includes('boundaryevent')) return 'boundary';
  return 'intermediate';
}

// Helper function to determine task type
function getTaskType(element: Element): string {
  const tagName = element.tagName.toLowerCase();
  if (tagName.includes('usertask')) return 'user';
  if (tagName.includes('servicetask')) return 'service';
  if (tagName.includes('scripttask')) return 'script';
  if (tagName.includes('sendtask')) return 'send';
  if (tagName.includes('receivetask')) return 'receive';
  if (tagName.includes('manualtask')) return 'manual';
  if (tagName.includes('businessruletask')) return 'business-rule';
  return 'user';
}

// Helper function to generate lane colors
function generateLaneColor(index: number): string {
  // CHANGE: Use a more professional, muted color palette
  const colors = [
    '#f5f5f5', // Light Gray
    '#e3f2fd', // Faint Blue
    '#e8eaf6', // Faint Indigo
    '#e0f2f1', // Faint Teal
    '#f1f8e9', // Faint Green
    '#fffde7', // Faint Yellow
  ];
  return colors[index % colors.length];
}

// Helper function to generate pool colors  
function generatePoolColor(index: number): string {
  const colors = [
    '#1F2937', // gray-800
    '#374151', // gray-700
    '#4B5563', // gray-600
    '#6B7280', // gray-500
  ];
  return colors[index % colors.length];
}

// Helper function to determine gateway type
function getGatewayType(element: Element): string {
  const tagName = element.tagName.toLowerCase();
  if (tagName.includes('exclusivegateway')) return 'exclusive';
  if (tagName.includes('parallelgateway')) return 'parallel';
  if (tagName.includes('inclusivegateway')) return 'inclusive';
  if (tagName.includes('eventbasedgateway')) return 'event-based';
  if (tagName.includes('complexgateway')) return 'complex';
  return 'exclusive';
}

export class BPMNService {
  /**
   * Import BPMN XML and convert to React Flow nodes and edges
   */
  static async importBPMN(xmlContent: string): Promise<{ nodes: Node[], edges: Edge[] }> {
    // Clean up the XML content - replace literal \n with actual newlines
    const cleanedXml = xmlContent.replace(/\\n/g, '\n').trim();
    
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(cleanedXml, 'text/xml');
    
    // Check for parsing errors
    const parserError = xmlDoc.querySelector('parsererror');
    if (parserError) {
      throw new Error('Invalid XML: ' + parserError.textContent);
    }
    
    // Check if we have a valid BPMN document
    const definitions = xmlDoc.querySelector('definitions');
    if (!definitions) {
      throw new Error('Invalid BPMN: No definitions element found');
    }

    const nodes: Node[] = [];
    const edges: Edge[] = [];
    
    // Get the process element
    const process = xmlDoc.querySelector('process');
    if (!process) {
      // CHANGE 1: Find process within a participant if it's not top-level
      const participant = xmlDoc.querySelector('participant');
      const processId = participant?.getAttribute('processRef');
      if (!processId || !xmlDoc.querySelector(`process[id="${processId}"]`)) {
        throw new Error('No process element found in BPMN file');
      }
    }

    // Get diagram information for positions - handle both namespaced and non-namespaced elements
    const shapes = xmlDoc.querySelectorAll('bpmndi\\:BPMNShape, BPMNShape');
    // CHANGE 2: The map will now store the full bounds object
    const shapePositions = new Map<string, { x: number, y: number, width: number, height: number }>();
    
    console.log(`Found ${shapes.length} shape definitions`);
    
    shapes.forEach((shape, index) => {
      const elementId = shape.getAttribute('bpmnElement');
      const bounds = shape.querySelector('omgdc\\:Bounds, Bounds');
      if (elementId && bounds) {
        const x = parseFloat(bounds.getAttribute('x') || '0');
        const y = parseFloat(bounds.getAttribute('y') || '0');
        // CHANGE 3: Extract and store width and height
        const width = parseFloat(bounds.getAttribute('width') || '100');
        const height = parseFloat(bounds.getAttribute('height') || '80');
        console.log(`Shape ${index}: ${elementId} at (${x}, ${y}) with size (${width}x${height})`);
        shapePositions.set(elementId, { x, y, width, height });
      }
    });
    
    console.log(`Extracted ${shapePositions.size} positions from diagram`);

    // Extract lane and pool information
    const lanes = new Map<string, { name: string, color: string, poolId?: string }>();
    const pools = new Map<string, { name: string, color: string, lanes: string[] }>();
    const elementLaneMap = new Map<string, string>(); // elementId -> laneId
    
    // Look for collaboration and pools first
    const collaboration = xmlDoc.querySelector('collaboration');
    const poolToLanesMap = new Map<string, string[]>();
    
    // Look for lanes in all processes (handle multiple processes)
    const allProcesses = xmlDoc.querySelectorAll('process');
    
    // First, find which process belongs to which pool
    const processToPoolMap = new Map<string, string>();
    if (collaboration) {
      const participants = collaboration.querySelectorAll('participant');
      participants.forEach(participant => {
        const poolId = participant.getAttribute('id');
        const processRef = participant.getAttribute('processRef');
        if (poolId && processRef) {
          processToPoolMap.set(processRef, poolId);
        }
      });
    }
    
    allProcesses.forEach(proc => {
      const processId = proc.getAttribute('id');
      const poolId = processId ? processToPoolMap.get(processId) : null;
      
      const laneElements = proc.querySelectorAll('lane');
      console.log(`🔍 Found ${laneElements.length} lane elements in process ${proc.id || 'unnamed'}`);
      
      laneElements.forEach((lane, index) => {
        const laneId = lane.getAttribute('id') || `lane_${index}`;
        const laneName = (lane.getAttribute('name') || `Lane ${index + 1}`).replace(/&#10;/g, '\n');
        const color = generateLaneColor(index);
        lanes.set(laneId, { name: laneName, color, poolId: poolId || undefined });
        
        // Track which lanes belong to which pool
        if (poolId && poolToLanesMap.has(poolId)) {
          poolToLanesMap.get(poolId)?.push(laneId);
        }
        
        console.log(`🏊 Lane ${index}: ${laneId} (${laneName}) - color: ${color}, pool: ${poolId || 'none'}`);
        
        // Map flow nodes to this lane
        const flowNodeRefs = lane.querySelectorAll('flowNodeRef');
        console.log(`   Found ${flowNodeRefs.length} flowNodeRefs in lane ${laneId}`);
        
        flowNodeRefs.forEach(ref => {
          const elementId = ref.textContent?.trim();
          if (elementId) {
            elementLaneMap.set(elementId, laneId);
            console.log(`   📋 Mapped ${elementId} → ${laneId}`);
          }
        });
      });
    });
    
    // Process pools (participants) in collaboration
    if (collaboration) {
      const participants = collaboration.querySelectorAll('participant');
      participants.forEach((participant, index) => {
        const poolId = participant.getAttribute('id') || `pool_${index}`;
        const poolName = (participant.getAttribute('name') || `Pool ${index + 1}`).replace(/&#10;/g, '\n');
        const color = generatePoolColor(index);
        pools.set(poolId, { name: poolName, color, lanes: [] });
        poolToLanesMap.set(poolId, []);
      });
    }
    
    // Create unified pool-with-lanes nodes
    poolToLanesMap.forEach((laneIds, poolId) => {
      const poolInfo = pools.get(poolId);
      const positionData = shapePositions.get(poolId) || { x: 50, y: 50, width: 600, height: 400 };
      
      // Collect lane information
      const poolLanes = laneIds.map((laneId, index) => {
        const laneInfo = lanes.get(laneId);
        const lanePosition = shapePositions.get(laneId);
        return {
          id: laneId,
          name: laneInfo?.name || `Lane ${index + 1}`,
          height: lanePosition?.height || 120,
          color: laneInfo?.color || generateLaneColor(index)
        };
      });
      
      // Create the unified pool-with-lanes node
      nodes.push({
        id: poolId,
        type: 'pool-with-lanes',
        position: { x: positionData.x, y: positionData.y },
        data: {
          id: poolId,
          label: poolInfo?.name || 'Pool',
          participant: poolInfo?.name || 'Pool',
          lanes: poolLanes,
          width: positionData.width,
          height: positionData.height,
          nodeType: 'pool-with-lanes' as const
        },
        zIndex: -20,
      });
    });
    
    // For pools without lanes, create simple pool nodes
    pools.forEach((poolInfo, poolId) => {
      if (!poolToLanesMap.has(poolId) || poolToLanesMap.get(poolId)?.length === 0) {
        const positionData = shapePositions.get(poolId) || { x: 50, y: 50, width: 600, height: 400 };
        nodes.push({
          id: poolId,
          type: 'pool-with-lanes',
          position: { x: positionData.x, y: positionData.y },
          data: {
            id: poolId,
            label: poolInfo.name,
            participant: poolInfo.name,
            lanes: [],
            width: positionData.width,
            height: positionData.height,
            nodeType: 'pool-with-lanes' as const
          },
          zIndex: -20,
        });
      }
    });
    
    console.log(`Found ${lanes.size} lanes and ${pools.size} pools`);
    console.log('Lane assignments:', Array.from(elementLaneMap.entries()));

    // Process all elements that have a shape in the diagram
    for (const id of shapePositions.keys()) {
      const element = xmlDoc.querySelector(`[id="${id}"]`);
      
      if (!element) {
        console.warn(`Shape found for element with ID ${id}, but the element itself was not found in the XML.`);
        continue;
      }

      const tagName = element.tagName.toLowerCase();
      const name = (element.getAttribute('name') || '').replace(/&#10;/g, '\n');
      
      // Skip lanes and pools as they are handled separately by the pool/lane processing logic
      if (tagName.includes('lane') || tagName.includes('participant')) {
        continue;
      }
      
      // Get position and dimensions from diagram
      const positionData = shapePositions.get(id)!; // We know it exists because we are iterating its keys
      const position = { x: positionData.x, y: positionData.y };
      
      // Check if element is assigned to a lane to determine its pool parent
      const assignedLaneId = elementLaneMap.get(id);
      const laneInfo = assignedLaneId ? lanes.get(assignedLaneId) : null;
      const poolId = laneInfo?.poolId;
      
      // A node's parent can be a pool (via a lane), another node (e.g., boundary events), or a sub-process
      let parentNodeId: string | undefined = poolId;
      const parentSubProcess = element.closest('subProcess');

      if (parentSubProcess) {
        const parentId = parentSubProcess.getAttribute('id');
        // A node cannot be its own parent
        if (parentId && parentId !== id) {
          parentNodeId = parentId;
        }
      }
      
      if (tagName.includes('boundaryevent')) {
        const attachedToRef = element.getAttribute('attachedToRef');
        if (attachedToRef) {
          parentNodeId = attachedToRef;
        }
      }

      // If a node has a parent, its position must be made relative to the parent
      if (parentNodeId) {
        const parentPosition = shapePositions.get(parentNodeId);
        if (parentPosition) {
          position.x -= parentPosition.x;
          position.y -= parentPosition.y;
        }
      }
      
      // Determine node type based on BPMN element type
      let nodeType = '';
      let nodeData: any = {
        label: name || id,
        laneId: assignedLaneId,
        laneName: laneInfo?.name,
        laneColor: laneInfo?.color,
      };
      
      if (tagName.includes('event')) {
        nodeType = 'event';
        nodeData.eventType = getEventType(element);
        
        // Check for message events
        if (element.querySelector('messageEventDefinition')) {
          nodeData.eventSubType = 'message';
        } else if (element.querySelector('timerEventDefinition')) {
          nodeData.eventSubType = 'timer';
        } else if (element.querySelector('errorEventDefinition')) {
          nodeData.eventSubType = 'error';
        } else if (element.querySelector('escalationEventDefinition')) {
          nodeData.eventSubType = 'escalation';
        }
      } else if (tagName.includes('task')) {
        nodeType = 'process';
        nodeData.processType = 'task';
        nodeData.taskType = getTaskType(element);
        
        // Check for documentation
        const documentation = element.querySelector('documentation');
        if (documentation) {
          nodeData.description = documentation.textContent?.trim();
        }
      } else if (tagName.includes('gateway')) {
        nodeType = 'gateway';
        nodeData.gatewayType = getGatewayType(element);

      } else if (tagName.includes('subprocess')) {
        nodeType = 'process';
        nodeData.processType = 'subprocess';
      } else if (tagName.includes('dataobjectreference')) {
        nodeType = 'data-object';
        nodeData.dataObjectType = 'data-object';
      } else if (tagName.includes('datastorereference')) {
        nodeType = 'data-object';
        nodeData.dataObjectType = 'data-store';
      } else if (tagName.includes('datainput') || tagName.includes('dataoutput')) {
        nodeType = 'data-object';
        nodeData.dataObjectType = 'data-object'; // Render as a document icon
      }
      
      if (nodeType) {
        const elementNode: any = {
          id,
          type: nodeType,
          position,
          data: nodeData,
          // All flow elements on top
          zIndex: 0,
        };
        
        // Assign element to its parent if it has one
        if (parentNodeId) {
          elementNode.parentNode = parentNodeId;
          elementNode.extent = 'parent';
          if (nodeData.eventType === 'boundary') {
            console.log(`🔗 Boundary event ${id} attached to parent task ${parentNodeId}`);
          } else {
            console.log(`🔗 Element ${id} assigned to parent pool ${parentNodeId}`);
          }
        }
        
        nodes.push(elementNode);
      }
    }
    
    // Process sequence flows as edges from all processes
    const allSequenceFlows: Element[] = [];
    allProcesses.forEach(p => allSequenceFlows.push(...Array.from(p.querySelectorAll('sequenceFlow'))));
    console.log(`Found ${allSequenceFlows.length} sequence flows`);
    allSequenceFlows.forEach((flow, index) => {
      const id = flow.getAttribute('id') || `flow_${index}`;
      const source = flow.getAttribute('sourceRef');
      const target = flow.getAttribute('targetRef');
      const name = flow.getAttribute('name') || '';
      
      console.log(`Flow ${index}: ${id}, ${source} -> ${target}, name: "${name}"`);
      
      if (source && target) {
        const edgeData: any = {
          label: name,
        };
        
        // Check for condition
        const condition = flow.querySelector('conditionExpression');
        if (condition && condition.textContent && condition.textContent.trim() !== 'None') {
          edgeData.condition = condition.textContent.trim();
        }
        
        // Determine correct handle IDs based on node types
        const sourceNode = nodes.find(n => n.id === source);
        const targetNode = nodes.find(n => n.id === target);
        
        let sourceHandle = 'output-right';
        let targetHandle = 'input-left';
        
        // Handle event nodes with specific handle naming
        if (sourceNode?.type === 'event') {
          const eventData = sourceNode.data as any;
          if (eventData.eventType === 'start') {
            sourceHandle = 'start-right';
          } else if (eventData.eventType === 'intermediate' || eventData.eventType === 'boundary') {
            sourceHandle = 'inter-output';
          }
        }
        
        if (targetNode?.type === 'event') {
          const eventData = targetNode.data as any;
          if (eventData.eventType === 'end') {
            targetHandle = 'end-left';
          } else if (eventData.eventType === 'intermediate' || eventData.eventType === 'boundary') {
            targetHandle = 'inter-input';
          }
        }
        
        console.log(`  Using handles: ${sourceHandle} -> ${targetHandle}`);

        edges.push({
          id,
          source,
          target,
          sourceHandle,
          targetHandle,
          type: 'sequence-flow',
          data: edgeData,
        });
      }
    });
    
    // Process data associations as edges
    const dataAssociations = xmlDoc.querySelectorAll('dataInputAssociation, dataOutputAssociation');
    console.log(`Found ${dataAssociations.length} data associations`);
    dataAssociations.forEach((assoc, index) => {
      const id = assoc.getAttribute('id') || `assoc_${index}`;
      const sourceRef = assoc.querySelector('sourceRef')?.textContent?.trim();
      const targetRef = assoc.querySelector('targetRef')?.textContent?.trim();

      console.log(`Data Association ${index}: ${id}, ${sourceRef} -> ${targetRef}`);

      let finalSource = sourceRef;
      let finalTarget = targetRef;
      const tagName = assoc.tagName.toLowerCase();

      // For associations, the source/target might be a dataInput/dataOutput inside a task.
      // We need to resolve this to the task itself for the visual connection.
      if (tagName.includes('datainputassociation')) {
        const targetElement = targetRef ? xmlDoc.querySelector(`[id="${targetRef}"]`) : null;
        const parentTask = targetElement?.closest('task, serviceTask, userTask, businessRuleTask, scriptTask, sendTask, receiveTask, manualTask');
        const parentTaskId = parentTask?.getAttribute('id');
        if (parentTaskId) {
          finalTarget = parentTaskId;
          console.log(`Remapped dataInputAssociation target from ${targetRef} to task ${finalTarget}`);
        }
      } else if (tagName.includes('dataoutputassociation')) {
        const sourceElement = sourceRef ? xmlDoc.querySelector(`[id="${sourceRef}"]`) : null;
        const parentTask = sourceElement?.closest('task, serviceTask, userTask, businessRuleTask, scriptTask, sendTask, receiveTask, manualTask');
        const parentTaskId = parentTask?.getAttribute('id');
        if (parentTaskId) {
          finalSource = parentTaskId;
          console.log(`Remapped dataOutputAssociation source from ${sourceRef} to task ${finalSource}`);
        }
      }

      if (finalSource && finalTarget) {
        edges.push({
          id,
          source: finalSource,
          target: finalTarget,
          type: 'association',
          markerEnd: { type: MarkerType.ArrowClosed },
          style: { strokeDasharray: '5 5' },
        });
      }
    });
    
    // Process message flows if any
    const messageFlows = xmlDoc.querySelectorAll('messageFlow');
    messageFlows.forEach((flow, index) => {
      const id = flow.getAttribute('id') || `msgflow_${index}`;
      const source = flow.getAttribute('sourceRef');
      const target = flow.getAttribute('targetRef');
      const name = flow.getAttribute('name') || '';
      
      if (source && target) {
        edges.push({
          id,
          source,
          target,
          sourceHandle: 'output-right',
          targetHandle: 'input-left',
          type: 'message-flow',
          data: { label: name, messageType: name },
        });
      }
    });
    
    console.log(`Import complete: ${nodes.length} nodes, ${edges.length} edges`);
    console.log('Nodes:', nodes.map(n => `${n.id}(${n.type})`));
    console.log('Edges:', edges.map(e => `${e.source}->${e.target}`));
    
    return { nodes, edges };
  }

  /**
   * Export React Flow nodes and edges to BPMN XML
   */
  static exportBPMN(nodes: Node[], edges: Edge[], processName: string = 'Process'): string {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const timestamp = new Date().toISOString();
    
    // Start building the XML
    let xml = `<?xml version="1.0" encoding="UTF-8"?>
<definitions xmlns="http://www.omg.org/spec/BPMN/20100524/MODEL"
             xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI"
             xmlns:omgdc="http://www.omg.org/spec/DD/20100524/DC"
             xmlns:omgdi="http://www.omg.org/spec/DD/20100524/DI"
             xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
             xmlns:signavio="http://www.signavio.com"
             id="definitions_${Date.now()}"
             targetNamespace="http://bpmn.io/schema/bpmn"
             exporter="Signavio Process Pipeline Creator"
             exporterVersion="1.0.0">
  
  <process id="process_${Date.now()}" name="${processName}" isExecutable="false">
`;

    // Add lanes and pools first - handle both separate lane/pool nodes AND pool-with-lanes nodes
    const lanes = nodes.filter(n => n.type === 'lane');
    const pools = nodes.filter(n => n.type === 'pool');
    const poolsWithLanes = nodes.filter(n => n.type === 'pool-with-lanes');
    
    // Handle separate pool nodes
    if (pools.length > 0) {
      pools.forEach(pool => {
        xml += `    <participant id="${pool.id}" name="${pool.data.label || pool.id}" processRef="process_${Date.now()}" />
`;
      });
    }
    
    // Handle pool-with-lanes nodes - extract lanes data
    const allLanes: Array<{id: string, name: string, flowNodeRefs: string[], poolId?: string}> = [];
    
    // Add lanes from separate lane nodes
    lanes.forEach(lane => {
      allLanes.push({
        id: lane.id,
        name: lane.data.label || lane.id,
        flowNodeRefs: [], // Will be populated later
        poolId: undefined
      });
    });
    
    // Add lanes from pool-with-lanes nodes
    poolsWithLanes.forEach(poolWithLanes => {
      if (poolWithLanes.data.lanes && poolWithLanes.data.lanes.length > 0) {
        poolWithLanes.data.lanes.forEach((laneData: any) => {
          allLanes.push({
            id: laneData.id,
            name: laneData.name,
            flowNodeRefs: [], // Will be populated later
            poolId: poolWithLanes.id
          });
        });
      }
    });
    
    // Find which nodes belong to which lanes
    nodes.forEach(node => {
      if (node.type === 'lane' || node.type === 'pool' || node.type === 'pool-with-lanes') return;
      
      // Check if node has lane assignment data
      if (node.data?.laneId) {
        const lane = allLanes.find(l => l.id === node.data.laneId);
        if (lane) {
          lane.flowNodeRefs.push(node.id);
        }
      }
      // Also check if node is positioned within a pool-with-lanes (child nodes)
      else if (node.parentNode) {
        const parentPool = poolsWithLanes.find(p => p.id === node.parentNode);
        if (parentPool && parentPool.data.lanes) {
          // Find which lane this node belongs to based on Y position
          const nodeY = node.position.y;
          let currentY = 0;
          for (const laneData of parentPool.data.lanes) {
            if (nodeY >= currentY && nodeY < currentY + laneData.height) {
              const lane = allLanes.find(l => l.id === laneData.id);
              if (lane) {
                lane.flowNodeRefs.push(node.id);
              }
              break;
            }
            currentY += laneData.height;
          }
        }
      }
    });
    
    // Export lanes if any exist
    if (allLanes.length > 0) {
      xml += '    <laneSet>\n';
      allLanes.forEach(lane => {
        xml += `      <lane id="${lane.id}" name="${lane.name}">
`;
        // Add flow node references
        lane.flowNodeRefs.forEach(nodeId => {
          xml += `        <flowNodeRef>${nodeId}</flowNodeRef>
`;
        });
        xml += '      </lane>\n';
      });
      xml += '    </laneSet>\n';
    }
    
    // Process other nodes
    nodes.forEach(node => {
      if (node.type === 'lane' || node.type === 'pool' || node.type === 'pool-with-lanes') return;
      
      const nodeId = node.id;
      const label = node.data.label || nodeId;
      
      // Get outgoing edges
      const outgoing = edges.filter(e => e.source === nodeId);
      const incoming = edges.filter(e => e.target === nodeId);
      
      switch (node.type) {
        case 'event':
          const eventType = node.data.eventType || 'start';
          const eventTag = eventType === 'start' ? 'startEvent' : 
                          eventType === 'end' ? 'endEvent' : 
                          'intermediateThrowEvent';
          
          xml += `    <${eventTag} id="${nodeId}" name="${label}">
`;
          
          // Add Signavio metadata
          xml += `      <extensionElements>
        <signavio:signavioMetaData metaKey="bgcolor" metaValue="#ffffff" />
        <signavio:signavioMetaData metaKey="bordercolor" metaValue="#000000" />
      </extensionElements>
`;
          
          // Add flows
          incoming.forEach(edge => {
            xml += `      <incoming>${edge.id}</incoming>
`;
          });
          outgoing.forEach(edge => {
            xml += `      <outgoing>${edge.id}</outgoing>
`;
          });
          
          // Add event definition if needed
          if (node.data.eventSubType === 'message') {
            xml += `      <messageEventDefinition id="msgDef_${nodeId}" />
`;
          } else if (node.data.eventSubType === 'timer') {
            xml += `      <timerEventDefinition id="timerDef_${nodeId}" />
`;
          }
          
          xml += `    </${eventTag}>
`;
          break;
          
        case 'process':
          const taskType = node.data.taskType || 'user';
          const taskTag = taskType === 'user' ? 'userTask' :
                         taskType === 'service' ? 'serviceTask' :
                         taskType === 'script' ? 'scriptTask' :
                         taskType === 'send' ? 'sendTask' :
                         taskType === 'receive' ? 'receiveTask' :
                         taskType === 'manual' ? 'manualTask' :
                         taskType === 'business-rule' ? 'businessRuleTask' :
                         'task';
          
          xml += `    <${taskTag} id="${nodeId}" name="${label}" implementation="##unspecified">
`;
          
          // Add documentation if present
          if (node.data.description) {
            xml += `      <documentation><![CDATA[${node.data.description}]]></documentation>
`;
          }
          
          // Add Signavio metadata
          xml += `      <extensionElements>
        <signavio:signavioMetaData metaKey="bgcolor" metaValue="#FFFFFF" />
        <signavio:signavioMetaData metaKey="bordercolor" metaValue="#000000" />
      </extensionElements>
`;
          
          // Add flows
          incoming.forEach(edge => {
            xml += `      <incoming>${edge.id}</incoming>
`;
          });
          outgoing.forEach(edge => {
            xml += `      <outgoing>${edge.id}</outgoing>
`;
          });
          
          xml += `    </${taskTag}>
`;
          break;
          
        case 'gateway':
          const gatewayType = node.data.gatewayType || 'exclusive';
          const gatewayTag = gatewayType === 'exclusive' ? 'exclusiveGateway' :
                            gatewayType === 'parallel' ? 'parallelGateway' :
                            gatewayType === 'inclusive' ? 'inclusiveGateway' :
                            gatewayType === 'event-based' ? 'eventBasedGateway' :
                            gatewayType === 'complex' ? 'complexGateway' :
                            'exclusiveGateway';
          
          xml += `    <${gatewayTag} id="${nodeId}" name="${label}" gatewayDirection="Diverging">
`;
          
          // Add Signavio metadata
          xml += `      <extensionElements>
        <signavio:signavioMetaData metaKey="bgcolor" metaValue="#ffffff" />
        <signavio:signavioMetaData metaKey="bordercolor" metaValue="#000000" />
      </extensionElements>
`;
          
          // Add flows
          incoming.forEach(edge => {
            xml += `      <incoming>${edge.id}</incoming>
`;
          });
          outgoing.forEach(edge => {
            xml += `      <outgoing>${edge.id}</outgoing>
`;
          });
          
          xml += `    </${gatewayTag}>
`;
          break;
          
        case 'data-object':
          xml += `    <dataObjectReference id="${nodeId}" name="${label}" dataObjectRef="dataObj_${nodeId}" />
    <dataObject id="dataObj_${nodeId}" />
`;
          break;
      }
    });
    
    // Add sequence flows
    edges.forEach(edge => {
      if (edge.type === 'message-flow') return; // Handle message flows separately
      
      const label = edge.data?.label || '';
      const condition = edge.data?.condition || '';
      
      xml += `    <sequenceFlow id="${edge.id}" name="${label}" sourceRef="${edge.source}" targetRef="${edge.target}">
`;
      
      // Add Signavio metadata
      xml += `      <extensionElements>
        <signavio:signavioMetaData metaKey="bordercolor" metaValue="#000000" />
      </extensionElements>
`;
      
      // Add condition if present
      if (condition) {
        xml += `      <conditionExpression xsi:type="tFormalExpression"><![CDATA[${condition}]]></conditionExpression>
`;
      }
      
      xml += `    </sequenceFlow>
`;
    });
    
    xml += '  </process>\n';
    
    // Add collaboration for message flows
    const messageFlows = edges.filter(e => e.type === 'message-flow');
    if (messageFlows.length > 0 || poolsWithLanes.length > 0) {
      xml += `  <collaboration id="collaboration_${Date.now()}">
`;
      
      // Add participants from separate pool nodes
      if (pools.length > 0) {
        pools.forEach(pool => {
          xml += `    <participant id="${pool.id}_ref" processRef="process_${Date.now()}" />
`;
        });
      }
      
      // Add participants from pool-with-lanes nodes
      if (poolsWithLanes.length > 0) {
        poolsWithLanes.forEach(poolWithLanes => {
          xml += `    <participant id="${poolWithLanes.id}" name="${poolWithLanes.data.label || poolWithLanes.data.participant || poolWithLanes.id}" processRef="process_${Date.now()}" />
`;
        });
      }
      
      // Add message flows
      messageFlows.forEach(flow => {
        xml += `    <messageFlow id="${flow.id}" name="${flow.data?.label || ''}" sourceRef="${flow.source}" targetRef="${flow.target}" />
`;
      });
      
      xml += '  </collaboration>\n';
    }
    
    // Add BPMN diagram information
    xml += `  <bpmndi:BPMNDiagram id="diagram_${Date.now()}">
    <bpmndi:BPMNPlane id="plane_${Date.now()}" bpmnElement="process_${Date.now()}">
`;
    
    // Add shapes
    nodes.forEach(node => {
      let width, height;
      
      if (node.type === 'pool-with-lanes') {
        // Use the stored dimensions for pool-with-lanes
        width = node.data?.width || 600;
        height = node.data?.height || 400;
      } else {
        width = node.data?.width || (node.type === 'gateway' ? 40 : node.type === 'event' ? 30 : 100);
        height = node.data?.height || (node.type === 'gateway' ? 40 : node.type === 'event' ? 30 : 80);
      }
      
      xml += `      <bpmndi:BPMNShape id="${node.id}_gui" bpmnElement="${node.id}">
        <omgdc:Bounds x="${Math.round(node.position.x)}" y="${Math.round(node.position.y)}" width="${width}" height="${height}" />
      </bpmndi:BPMNShape>
`;
    });
    
    // Add edges (simplified - would need proper waypoint calculation)
    edges.forEach(edge => {
      const sourceNode = nodes.find(n => n.id === edge.source);
      const targetNode = nodes.find(n => n.id === edge.target);
      
      if (sourceNode && targetNode) {
        xml += `      <bpmndi:BPMNEdge id="${edge.id}_gui" bpmnElement="${edge.id}">
        <omgdi:waypoint x="${Math.round(sourceNode.position.x + 50)}" y="${Math.round(sourceNode.position.y + 40)}" />
        <omgdi:waypoint x="${Math.round(targetNode.position.x)}" y="${Math.round(targetNode.position.y + 40)}" />
      </bpmndi:BPMNEdge>
`;
      }
    });
    
    xml += `    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</definitions>`;
    
    return xml;
  }
}